import { api } from '../../../api/client';
import type {
  RegisterResult, RegisterSuboutRequest, SuboutLookups, SuboutRegistrationKey,
  SuboutRegistrationRow, SuboutSampleDetailRow, SuboutTestOrderActionResult,
  SuboutTestOrderDetail, UpdateTestOrderRequest,
} from './types';

// Sample SubContract Tracking → Subout Registration — thin axios wrappers over
// /api/v1/subout-registration. The shared client injects the JWT + loader.

const BASE = '/api/v1/subout-registration';

/** The registration queue for a job Received-date window (yyyy-MM-dd, inclusive;
 * an omitted bound is open-ended). `silent` suppresses the global loader for the
 * background option fetch. */
export async function fetchSuboutQueue(
  from?: string | null, to?: string | null, silent = false,
): Promise<SuboutRegistrationRow[]> {
  const res = await api.get(BASE + '/queue', {
    params: { from: from || undefined, to: to || undefined },
    ...(silent ? { silentLoading: true } : {}),
  });
  return (res.data as { data: SuboutRegistrationRow[] }).data ?? [];
}

/** Contract labs + TAT + preservatives + delivery services for the dialog. */
export async function fetchSuboutLookups(): Promise<SuboutLookups> {
  const res = await api.get(BASE + '/lookups');
  return (res.data as { data: SuboutLookups }).data;
}

/** The selected pending samples for the detail view's "Subout Samples" grid. */
export async function fetchSampleDetails(keys: SuboutRegistrationKey[]): Promise<SuboutSampleDetailRow[]> {
  const res = await api.post(BASE + '/sample-details', keys);
  return (res.data as { data: SuboutSampleDetailRow[] }).data ?? [];
}

/** Register the selected pending tests into one new subout order. */
export async function registerSubout(request: RegisterSuboutRequest): Promise<RegisterResult> {
  const res = await api.post(BASE + '/register', request);
  return (res.data as { data: RegisterResult }).data;
}

// ── Subout Test Order — Edit / Delete / Submit ──────────────────────────────
const TO = '/api/v1/subout-test-order';

/** One order's header for the Edit detail view. */
export async function fetchTestOrder(oid: string): Promise<SuboutTestOrderDetail> {
  const res = await api.get(`${TO}/order/${oid}`);
  return (res.data as { data: SuboutTestOrderDetail }).data;
}

/** An order's linked samples (the Subout Samples grid). */
export async function fetchTestOrderSamples(oid: string): Promise<SuboutSampleDetailRow[]> {
  const res = await api.get(`${TO}/order/${oid}/samples`);
  return (res.data as { data: SuboutSampleDetailRow[] }).data ?? [];
}

/** Edit → Save: update the order's header; returns the refreshed order. */
export async function updateTestOrder(oid: string, req: UpdateTestOrderRequest): Promise<SuboutTestOrderDetail> {
  const res = await api.put(`${TO}/order/${oid}`, req);
  return (res.data as { data: SuboutTestOrderDetail }).data;
}

/** Submit the selected orders (bulk). */
export async function submitTestOrders(orderOids: string[]): Promise<{ submitted: number; rows: SuboutTestOrderActionResult[] }> {
  const res = await api.post(`${TO}/submit`, orderOids);
  return (res.data as { data: { submitted: number; rows: SuboutTestOrderActionResult[] } }).data;
}

/** Delete the selected orders (bulk). */
export async function deleteTestOrders(orderOids: string[]): Promise<{ deleted: number; rows: SuboutTestOrderActionResult[] }> {
  const res = await api.post(`${TO}/delete`, orderOids);
  return (res.data as { data: { deleted: number; rows: SuboutTestOrderActionResult[] } }).data;
}

/** Pull the human message out of an axios error, else a fallback. */
export function apiMessage(e: unknown, fallback: string): string {
  const resp = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return resp?.data?.error?.message ?? fallback;
}
